import { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { renderAsync } from 'docx-preview';
import { RootState } from 'store';
import { mergeIntegrationService } from 'services/mergeIntegration';
import { AtsCandidate } from './AtsCandidates';

export const useResumeHandler = () => {
    const expertId = useSelector((state: RootState) => state.auth.user.expertId);
    const [showResumeModal, setShowResumeModal] = useState(false);
    const [signedResumeUrl, setSignedResumeUrl] = useState<string | null>(null);
    const [isDocx, setIsDocx] = useState(false);
    const [loading, setLoading] = useState(false);
    const docxContainerRef = useRef<HTMLDivElement>(null);

    // Helper function to get signed resume URL
    const getSignedUrl = async (s3Url: string): Promise<string | null> => {
        try {
            console.log('Fetching signed URL for:', s3Url);
            const response = await mergeIntegrationService.getSignedResumeUrl({
                expertId,
                s3Url
            });

            console.log('Signed URL response:', response);

            if (response?.apiStatus === "SUCCESS" && response?.output?.signed_url) {
                console.log('Signed URL generated successfully:', response.output.signed_url);
                return response.output.signed_url;
            } else {
                console.error('Failed to generate signed URL. Response:', response);
                toast.error("Failed to generate signed resume URL");
                return null;
            }
        } catch (error) {
            console.error("Error fetching signed resume URL:", error);
            toast.error("Error accessing resume");
            return null;
        }
    };

    const handleViewResume = async (candidate: AtsCandidate) => {
        if (!candidate?.resume_url) {
            console.error("Resume URL is undefined or empty.");
            return;
        }

        console.log('View resume clicked. Original URL:', candidate.resume_url);

        try {
            // Get signed URL to avoid CORS issues
            const signedUrl = await getSignedUrl(candidate.resume_url);
            if (!signedUrl) return;

            const urlPath = candidate.resume_url.split('?')[0];
            const fileExtension = urlPath.split('.').pop()?.toLowerCase();

            if (!fileExtension) {
                console.error("Unable to determine the file extension.");
                return;
            }

            setShowResumeModal(true);
            setLoading(true);

            if (fileExtension === 'docx') {
                setIsDocx(true);
                try {
                    const response = await fetch(signedUrl);
                    if (!response.ok) {
                        throw new Error(`Failed to fetch the resume. Status: ${response.status}`);
                    }
                    const arrayBuffer = await response.arrayBuffer();
                    if (docxContainerRef.current) {
                        await renderAsync(arrayBuffer, docxContainerRef.current);
                    }
                } catch (error) {
                    console.error('Error loading DOCX file:', error);
                    toast.error("Failed to load resume");
                } finally {
                    setLoading(false);
                }
            } else {
                setIsDocx(false);
                // Store signed URL for PDF viewer
                setSignedResumeUrl(signedUrl);
                setLoading(false);
            }
        } catch (error) {
            console.error("An error occurred in handleViewResume:", error);
        }
    };

    const handleDownloadResume = async (candidate: AtsCandidate) => {
        if (!candidate?.resume_url) return;

        console.log('Download resume clicked. Original URL:', candidate.resume_url);
        // Get signed URL to avoid CORS issues
        const signedUrl = await getSignedUrl(candidate.resume_url);
        if (!signedUrl) return;

        console.log('Downloading resume from signed URL:', signedUrl);

        try {
            // Fetch the file
            const response = await fetch(signedUrl);
            if (!response.ok) {
                throw new Error(`Failed to download resume. Status: ${response.status}`);
            }

            const blob = await response.blob();

            // Get file extension from original URL
            const urlPath = candidate.resume_url.split('?')[0];
            const fileExtension = urlPath.split('.').pop()?.toLowerCase() || 'pdf';

            // Create custom filename using candidate's name
            const filename = `${candidate.first_name}_${candidate.last_name}_Resume.${fileExtension}`;

            // Create download link and trigger download
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            console.log('Resume downloaded successfully as:', filename);
        } catch (error) {
            console.error('Error downloading resume:', error);
            toast.error('Failed to download resume');
        }
    };

    const handleCloseResumeModal = () => {
        setShowResumeModal(false);
        setSignedResumeUrl(null);
    };

    return {
        showResumeModal,
        signedResumeUrl,
        isDocx,
        loading,
        docxContainerRef,
        handleViewResume,
        handleDownloadResume,
        handleCloseResumeModal,
    };
};
